import React, { useEffect, useState, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { analyticsDashboardSaga } from "./saga";
import * as selectors from "./selectors";
import { useAuth } from "../../../../contexts/auth";
import "./style.css";
import { Select, DatePicker } from "antd";
import OptionsLogo from "../../../../../images/options.svg";
import OrdersIMG from "../../../../../images/orders.png";
import CompletedIMG from "../../../../../images/completed.png";
import DisputeIMG from "../../../../../images/dispute.png";
import LabIMG from "../../../../../images/lab.png";
import PendingIMG from "../../../../../images/pending.png";
import PickupIMG from "../../../../../images/pickup.png";
import RegrettedIMG from "../../../../../images/regretted.png";
import RejectedIMG from "../../../../../images/rejected.png";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import moment from "moment";
import Loader from "../../../../components/Loader";

const { Option } = Select;

const CardContainer = ({ image, header, count }) => {
  return (
    <div className="dashboard-cards">
      <div className="dashboard-cards-content">
        <div>
          <img
            src={image}
            alt=""
            style={{ marginRight: "19px" }}
            className="dashboard-card-content-images"
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column" }}>
          <div className="dashboard-cards-headline">{header}</div>
          <div className="dashboard-cards-count">{count}</div>
        </div>
      </div>
    </div>
  );
};

const ChartContainer = ({
  width,
  data,
  header,
  sideText,
  dataKeyX,
  dataKeyY,
}) => {
  return (
    <div className="dashboard-single-chart-container">
      <div className="dashboard-charts-headline">
        <p className="cartesian-graph-header">{header}</p>
        <p
          style={{
            fontSize: "14px",
            textAlign: "right",
            fontFamily: "Roboto, sans-serif",
            letterSpacing: "0px",
            color: "#616161",
            opacity: 1,
          }}
        >
          {sideText}
        </p>
      </div>
      <BarChart
        width={width}
        height={300}
        data={data}
        layout="vertical"
        barGap={20}
        barSize={31}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          type="number"
          dataKey={dataKeyX}
          dy={5}
          axisLine={false}
          tickLine={false}
        />
        <YAxis type="category" dataKey={dataKeyY} dx={7} tickLine={false} />
        <Tooltip />
        <Legend />
        <Bar dataKey={dataKeyX} fill="#0a8080" label={{ position: "right" }} />
      </BarChart>
    </div>
  );
};

const AnalyticsDashboard = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: analyticsDashboardSaga });
  const dispatch = useDispatch();
  const auth = useAuth();
  const user = auth.loggedInUser;
  const currentDate = moment();
  //const [currentDate, setCurrentDate] = useState(new Date());

  const [visibilityForSmallerScreens, setVisibilityForSmallerScreens] =
    useState(false);

  const [userSelected, setUserSelected] = useState(
    !auth.isCompanyAdmin ? user._id : null
  );
  const [selectedFromDate, setSelectedFromDate] = useState(
    moment(currentDate).subtract(15, 'days')
      .format()
      .split("+")[0]
  );
  const [selectedToDate, setSelectedToDate] = useState(
    moment(currentDate).format().split("+")[0]
  );
  const [fromDate, setFromDate] = useState(moment(currentDate).subtract(15, 'days'));
  const [toDate, setToDate] = useState(moment(currentDate));
  const [width, setWidth] = useState(0);

  const disabledFromDate = (current) => {
    if (current.format().split('+')[0] > moment(currentDate).format().split("+")[0] || current.format().split("+")[0] > selectedToDate) {
      return true;
    }
    return (
      current &&
      current >
        moment(
          moment(currentDate).add(1, 'days')
            .format()
            .split("+")[0]
        )
    );
  };

  const disabledToDate = (current) => {
    return current && current < moment(selectedFromDate);
  };
  useEffect(() => {
    if (auth.isCompanyAdmin) {
      dispatch(actions.fetchCompanyUsers({ id: user.company._id }));
    }
  }, [dispatch, auth, user]);

  useEffect(() => {
    let query = {};
    let from = `${selectedFromDate.split("T")[0]}T00:00:00`;
    let to = `${selectedToDate.split("T")[0]}T23:59:59`;
    //if (selectedFromDate !== null && selectedToDate !== null) {
    query = {
      createdAtFrom: from,
      createdAtTo: to,
    };

    //} else {
    // query = {
    //   createdAtFrom: moment("Sun, 08 Aug 2021 05:51:36 GMT")
    //     .format()
    //     .split(" ")[0]
    //     .split("+")[0],
    //   createdAtTo: moment("Tue, 08 Aug 2023 07:23:36 GMT")
    //     .format()
    //     .split(" ")[0]
    //     .split("+")[0],
    // };
    //}
    if (
      //(selectedFromDate === null && selectedToDate === null) ||
      selectedFromDate !== null &&
      selectedToDate !== null
    ) {
      if (userSelected) query.user = userSelected;

      dispatch(actions.fetchTopCommodities(query));
      dispatch(actions.fetchTopPackages(query));
      dispatch(actions.fetchTATDetailsPackage(query));
      dispatch(actions.fetchTATDetailsCommodity(query));
      dispatch(actions.fetchOrdersInfoForAnalytics(query));
    }
  }, [dispatch, userSelected, selectedFromDate, selectedToDate]);

  useLayoutEffect(() => {
    function updateCheck() {
      setVisibilityForSmallerScreens(false);
      if (window.innerWidth > 1100) setWidth(450);
      else if (window.innerWidth > 820 && window.innerWidth < 900)
        setWidth(window.innerWidth - 500);
      else if (window.innerWidth < 820 && window.innerWidth > 400)
        setWidth(window.innerWidth - 150);
      else if (window.innerWidth < 400) setWidth(285);
      else setWidth(window.innerWidth - 600);
    }
    window.addEventListener("resize", updateCheck);
    updateCheck();
    return () => window.removeEventListener("resize", updateCheck);
  }, []);

  let topCommodities = useSelector(selectors.selectTopCommodities) || [];
  let topPackages = useSelector(selectors.selectTopPackages) || [];
  let tatDetailsPackages =
    useSelector(selectors.selectTATDetailsOnPackages) || [];
  let tatDetailsCommodities =
    useSelector(selectors.selectTATDetailsOnCommodities) || [];
  let ordersDetails = useSelector(selectors.selectOrdersDetails);
  const users = useSelector(selectors.selectUsers) || [];
  const loading = useSelector(selectors.selectLoading);

  let usersData = [...users, { label: "All Users", value: null }];

  topCommodities = topCommodities.slice(0, 5);
  topPackages = topPackages.slice(0, 5);
  tatDetailsPackages = tatDetailsPackages.slice(0, 5);
  tatDetailsCommodities = tatDetailsCommodities.slice(0, 5);

  tatDetailsPackages = tatDetailsPackages.map((obj) => {
    return {
      name: obj.packageName,
      days: obj.avgTime,
    };
  });
  tatDetailsCommodities = tatDetailsCommodities.map((obj) => {
    return {
      name: obj._id.name,
      days: obj.avgTime,
    };
  });

  const cardsData1 = [
    {
      image: OrdersIMG,
      header: "Total Orders",
      count: ordersDetails?.totalOrders,
    },
    {
      image: CompletedIMG,
      header: "Completed",
      count: ordersDetails?.completedOrders,
    },
    {
      image: PendingIMG,
      header: "Pending",
      count: ordersDetails?.pendingOrders,
    },
    {
      image: LabIMG,
      header: "Assigned To Lab",
      count: ordersDetails?.ordersAssignedToLab,
    },
    {
      image: RejectedIMG,
      header: "Rejected By Labs",
      count: ordersDetails?.ordersRejectedByLab,
    },
  ];
  const cardsData2 = [
    {
      image: PickupIMG,
      header: "Out For Pickup",
      count: ordersDetails?.ordersOutForPickup,
    },
    {
      image: LabIMG,
      header: "Report Uploaded",
      count: ordersDetails?.ordersReportUploaded,
    },
    {
      image: RegrettedIMG,
      header: "Orders Regretted",
      count: ordersDetails?.ordersRegretted,
    },
    {
      image: DisputeIMG,
      header: "Under Dispute",
      count: ordersDetails?.ordersUnderDispute,
    },
    {
      image: PendingIMG,
      header: "Payment Pending",
      count: ordersDetails?.ordersPaymentPending,
    },
  ];

  const cardsData3 = [
    {
      image: OrdersIMG,
      header: "Total Orders",
      count: ordersDetails?.totalOrders,
    },
    {
      image: CompletedIMG,
      header: "Completed",
      count: ordersDetails?.completedOrders,
    },
    {
      image: PendingIMG,
      header: "Pending",
      count: ordersDetails?.pendingOrders,
    },
    {
      image: LabIMG,
      header: "Assigned To Lab",
      count: ordersDetails?.ordersAssignedToLab,
    },
    {
      image: LabIMG,
      header: "Report Uploaded",
      count: ordersDetails?.ordersReportUploaded,
    },
    {
      image: PickupIMG,
      header: "Out For Pickup",
      count: ordersDetails?.ordersOutForPickup,
    },
    {
      image: RejectedIMG,
      header: "Rejected By Labs",
      count: ordersDetails?.ordersRejectedByLab,
    },
    {
      image: RegrettedIMG,
      header: "Orders Regretted",
      count: ordersDetails?.ordersRegretted,
    },

    {
      image: RejectedIMG,
      header: "Rejected By Labs",
      count: ordersDetails?.ordersRejectedByLab,
    },
    {
      image: DisputeIMG,
      header: "Under Dispute",
      count: ordersDetails?.ordersUnderDispute,
    },
    {
      image: PendingIMG,
      header: "Payment Pending",
      count: ordersDetails?.ordersPaymentPending,
    },
  ];

  if (loading) return <Loader />;
  return (
    <div>
      <div className="dashboard-top">
        <p className="dashboard-header">Dashboard</p>
        <div className="dashboard-options-img">
          <img
            src={OptionsLogo}
            alt=""
            onClick={() => {
              setVisibilityForSmallerScreens(!visibilityForSmallerScreens);
            }}
          />
        </div>

        <div
          className={
            auth.isCompanyAdmin
              ? `dashboard-selectors`
              : "dashboard-selectors-adjust-width"
          }
        >
          {auth.isCompanyAdmin && (
            <div>
              <Select
                placeholder="Select an operator"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                onChange={(value) => {
                  setUserSelected(value);
                }}
                value={userSelected}
                style={{ width: "172px" }}
              >
                {usersData.map((user, index) => (
                  <Option key={index} value={user.value}>{user.label}</Option>
                ))}
              </Select>
            </div>
          )}

          <div>
            <DatePicker
              placeholder="From"
              onChange={(date) => {
                setFromDate(date);
                setSelectedFromDate(date.format().split("+")[0]);
              }}
              value={fromDate}
              disabledDate={disabledFromDate}
            />
          </div>
          <div>
            <DatePicker
              placeholder="To"
              onChange={(date) => {
                setToDate(date);
                console.log(date.format().split("+")[0]);
                setSelectedToDate(date.format().split("+")[0]);
              }}
              value={toDate}
              disabledDate={disabledToDate}
            />
          </div>
        </div>
      </div>
      {visibilityForSmallerScreens && (
        <div
          className={
            auth.isCompanyAdmin
              ? `dashboard-selectors-small-devices`
              : `dashboard-selectors-small-devices-adjust-height`
          }
          style={{
            opacity: !visibilityForSmallerScreens ? "0" : "1",
            transition: "ease-in-out 3s",
          }}
        >
          {auth.isCompanyAdmin && (
            <div>
              <Select
                placeholder="Select an operator"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                onChange={(value) => {
                  setUserSelected(value);
                }}
                value={userSelected}
                style={{ width: "100%" }}
              >
                {usersData.map((user, index) => (
                  <Option key={index} value={user.value}>{user.label}</Option>
                ))}
              </Select>
            </div>
          )}

          <div>
            <DatePicker
              placeholder="From"
              style={{ width: "100%" }}
              onChange={(date) => {
                setFromDate(date);
                setSelectedFromDate(date.format().split("+")[0]);
              }}
              value={fromDate}
              disabledDate={disabledFromDate}
            />
          </div>
          <div>
            <DatePicker
              placeholder="To"
              style={{ width: "100%" }}
              onChange={(date) => {
                setToDate(date);
                setSelectedToDate(date.format().split("+")[0]);
              }}
              value={toDate}
              disabledDate={disabledToDate}
            />
          </div>
        </div>
      )}
      <div className="dashboard-cards-large-screens">
        <div className="dashboard-cards-container-2">
          {cardsData1.map((obj, index) => (
            <CardContainer
              image={obj.image}
              header={obj.header}
              count={obj.count}
              key={index}
            />
          ))}
        </div>
        <div className="dashboard-cards-container-2">
          {cardsData2.map((obj, index) => (
            <CardContainer
              image={obj.image}
              header={obj.header}
              count={obj.count}
              key={index}
            />
          ))}
        </div>
      </div>
      <div className="dashboard-cards-small-screens">
        {cardsData3.map((obj, index) => (
          <CardContainer
            image={obj.image}
            header={obj.header}
            count={obj.count}
            key={index}
          />
        ))}
      </div>

      <div className="dashboard-charts-container">
        <ChartContainer
          width={width}
          data={topCommodities}
          header="Top Commodities"
          sideText="- By number of orders"
          dataKeyX="count"
          dataKeyY="commodityName"
        />
        <ChartContainer
          width={width}
          data={topPackages}
          header="Top Packages"
          sideText="- By number of orders"
          dataKeyX="count"
          dataKeyY="packageName"
        />
      </div>
      <div className="dashboard-charts-container">
        <ChartContainer
          width={width}
          data={tatDetailsCommodities}
          header="Top Commodities"
          sideText="- By Avg. TAT in (Days)"
          dataKeyX="days"
          dataKeyY="name"
        />

        <ChartContainer
          width={width}
          data={tatDetailsPackages}
          header="Top Packages"
          sideText="- By Avg. TAT in (Days)"
          dataKeyX="days"
          dataKeyY="name"
        />
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
